import { useEffect, useState, useRef } from 'react'

const CHUNK_ERROR_PATTERNS = [
  'failed to fetch dynamically imported module',
  'loading chunk',
  'chunkloaderror',
  'failed to load module script',
  'error loading dynamically imported module'
]

function isChunkLoadError(error) {
  const message = error?.message || error?.reason?.message || String(error)
  const lowerMessage = message.toLowerCase()
  return CHUNK_ERROR_PATTERNS.some(pattern => lowerMessage.includes(pattern))
}

const RELOAD_DELAY = 5000 // 5 seconds to read the message

export default function ChunkLoadErrorHandler() {
  const [showReloading, setShowReloading] = useState(false)
  const reloadTimeoutRef = useRef(null)
  const hasHandledRef = useRef(false)

  useEffect(() => {
    const triggerReload = (event) => {
      // Prevent duplicate handling (both error and unhandledrejection can fire)
      if (hasHandledRef.current) return
      hasHandledRef.current = true

      event.preventDefault()
      setShowReloading(true)
      reloadTimeoutRef.current = setTimeout(() => window.location.reload(), RELOAD_DELAY)
    }

    const handleError = (event) => {
      if (isChunkLoadError(event.error || event)) {
        triggerReload(event)
      }
    }

    const handleUnhandledRejection = (event) => {
      if (isChunkLoadError(event.reason)) {
        triggerReload(event)
      }
    }

    window.addEventListener('error', handleError)
    window.addEventListener('unhandledrejection', handleUnhandledRejection)

    return () => {
      window.removeEventListener('error', handleError)
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
      if (reloadTimeoutRef.current) {
        clearTimeout(reloadTimeoutRef.current)
      }
    }
  }, [])

  if (!showReloading) return null

  return (
    <div className="chunk-reload-overlay" role="alert" aria-live="assertive">
      <div className="chunk-reload-card">
        <div className="chunk-reload-spinner" aria-hidden="true" />
        <h3 className="chunk-reload-title">New Version Available</h3>
        <p className="chunk-reload-message">
          The app has been updated. Reloading to get the latest version...
        </p>
      </div>
    </div>
  )
}
